import { useState, useEffect } from "react"
import { collection, getDocs, doc, setDoc, deleteDoc, addDoc, updateDoc, getDoc } from "firebase/firestore"
import { HiOutlineTrash, HiOutlinePencil } from "react-icons/hi"
import { db } from "../firebaseconfig"
import "../css/areaControl.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch } from "@fortawesome/free-solid-svg-icons"
import Swal from "sweetalert2"

const removeAccents = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

export default function AreaControlPage() {
  const [areas, setAreas] = useState([])
  const [formData, setFormData] = useState({ name: "", type: "area", amount: "", from: "" })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingArea, setEditingArea] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  // Update 3: Removed amountQuery state
  const [filteredAreas, setFilteredAreas] = useState([])

  useEffect(() => {
    fetchAreas()
  }, [])

  useEffect(() => {
    const filtered = areas.filter((area) => {
      const nameMatch = removeAccents(area.name.toLowerCase()).includes(removeAccents(searchQuery.toLowerCase()))
      const amountMatch = area.amount.toString().includes(searchQuery)
      return nameMatch || amountMatch
    })
    setFilteredAreas(filtered)
  }, [areas, searchQuery]) // Update 1: Modified filter function and dependency array

  const fetchAreas = async () => {
    try {
      const areaControllerCollection = collection(db, "areacontroller")
      const areaControllerSnapshot = await getDocs(areaControllerCollection)
      const areaControllerData = areaControllerSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      const budgetsCollection = collection(db, "budgets")
      const budgetsSnapshot = await getDocs(budgetsCollection)
      const budgetsData = budgetsSnapshot.docs.reduce((acc, doc) => {
        acc[doc.id] = doc.data()
        return acc
      }, {})

      const combinedData = areaControllerData.map((area) => ({
        ...area,
        amount: budgetsData[area.name]?.amount || area.amount,
        used: budgetsData[area.name]?.used || area.used,
      }))

      setAreas(combinedData)
      setFilteredAreas(combinedData)
    } catch (error) {
      console.error("Error fetching areas:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar las áreas y subáreas",
      })
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    const sanitizeInput = (input) => {
      return input
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "amount" ? Number.parseFloat(value) || 0 : sanitizeInput(value),
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      formData.name = formData.name

      if (formData.type === "subarea" && !formData.from) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Debe seleccionar una secretaría para el área",
        })
        return
      }

      const newAreaCollection = collection(db, formData.name)

      await setDoc(doc(newAreaCollection, "metadata"), {
        type: formData.type,
        amount: Number.parseFloat(formData.amount) || 0,
        from: formData.type === "subarea" ? formData.from : null,
        used: 0,
      })

      const areaControllerCollection = collection(db, "areacontroller")
      await addDoc(areaControllerCollection, {
        name: formData.name,
        type: formData.type,
        amount: Number.parseFloat(formData.amount) || 0,
        from: formData.type === "subarea" ? formData.from : null,
        used: 0,
      })

      const budgetsCollection = collection(db, "budgets")
      await setDoc(doc(budgetsCollection, formData.name), {
        type: formData.type,
        amount: Number.parseFloat(formData.amount) || 0,
        from: formData.type === "subarea" ? formData.from : null,
        used: 0,
      })

      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: `${formData.type === "area" ? "Área" : "Subárea"} creada exitosamente`,
      })
      setFormData({ name: "", type: "area", amount: "", from: "" })
      setShowCreateModal(false)
      fetchAreas()
    } catch (error) {
      console.error("Error creating area:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al crear el área/subárea",
      })
    }
  }

  const handleEdit = async (area) => {
    try {
      const budgetDoc = await getDoc(doc(db, "budgets", area.name))
      const budgetData = budgetDoc.data()

      setEditingArea(area)
      setFormData({
        name: area.name,
        type: area.type,
        amount: budgetData?.amount || area.amount,
        from: area.from || "",
      })
      setShowEditModal(true)
    } catch (error) {
      console.error("Error fetching budget data:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los datos del presupuesto",
      })
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    try {
      formData.name = formData.name.toLowerCase()
      editingArea.name = editingArea.name.toLowerCase()

      if (formData.type === "subarea" && !formData.from) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Debe seleccionar una secretaría para el área.",
        })
        return
      }

      const oldAreaCollection = collection(db, editingArea.name)
      const newAreaCollection = collection(db, formData.name.toLowerCase())

      if (editingArea.name !== formData.name) {
        await setDoc(doc(newAreaCollection, "metadata"), {
          type: formData.type,
          amount: Number.parseFloat(formData.amount) || 0,
          from: formData.type === "subarea" ? formData.from : null,
          used: editingArea.used || 0,
        })

        const oldDocs = await getDocs(oldAreaCollection)
        oldDocs.forEach(async (document) => {
          if (document.id !== "metadata") {
            await setDoc(doc(newAreaCollection, document.id), document.data())
          }
        })

        const oldDocs2 = await getDocs(oldAreaCollection)
        oldDocs2.forEach(async (document) => {
          await deleteDoc(doc(oldAreaCollection, document.id))
        })
      } else {
        await updateDoc(doc(oldAreaCollection, "metadata"), {
          type: formData.type,
          amount: Number.parseFloat(formData.amount) || 0,
          from: formData.type === "subarea" ? formData.from : null,
          used: editingArea.used || 0,
        })
      }

      const areaControllerCollection = collection(db, "areacontroller")
      const querySnapshot = await getDocs(areaControllerCollection)
      querySnapshot.forEach(async (document) => {
        if (document.data().name === editingArea.name) {
          await updateDoc(document.ref, {
            name: formData.name.toLowerCase(),
            type: formData.type,
            amount: Number.parseFloat(formData.amount) || 0,
            from: formData.type === "subarea" ? formData.from.toLowerCase() : null,
            used: editingArea.used || 0,
          })
        }
      })

      const budgetsCollection = collection(db, "budgets")
      if (editingArea.name !== formData.name) {
        await setDoc(doc(budgetsCollection, formData.name.toLowerCase()), {
          type: formData.type,
          amount: Number.parseFloat(formData.amount) || 0,
          from: formData.type === "subarea" ? formData.from.toLowerCase() : null,
          used: editingArea.used || 0,
        })
        await deleteDoc(doc(budgetsCollection, editingArea.name))
      } else {
        await updateDoc(doc(budgetsCollection, formData.name.toLowerCase()), {
          type: formData.type,
          amount: Number.parseFloat(formData.amount) || 0,
          from: formData.type === "subarea" ? formData.from.toLowerCase() : null,
          used: editingArea.used || 0,
        })
      }

      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: `${formData.type === "area" ? "Área" : "Subárea"} actualizada exitosamente`,
      })
      setShowEditModal(false)
      fetchAreas()
    } catch (error) {
      console.error("Error updating area:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al actualizar el área/subárea",
      })
    }
  }

  const handleDelete = async (areaId) => {
    Swal.fire({
      title: "¿Está seguro?",
      text: "¿Desea eliminar esta área/subárea? Esta acción eliminará todos los datos asociados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const areaToDelete = areas.find((area) => area.id === areaId)

          const areaCollection = collection(db, areaToDelete.name)
          const querySnapshot = await getDocs(areaCollection)
          querySnapshot.forEach(async (document) => {
            await deleteDoc(doc(areaCollection, document.id))
          })

          const areaControllerCollection = collection(db, "areacontroller")
          const areaControllerQuerySnapshot = await getDocs(areaControllerCollection)
          areaControllerQuerySnapshot.forEach(async (document) => {
            if (document.data().name === areaToDelete.name) {
              await deleteDoc(document.ref)
            }
          })

          const budgetsCollection = collection(db, "budgets")
          await deleteDoc(doc(budgetsCollection, areaToDelete.name))

          Swal.fire("Eliminado", "Secretaría/Área eliminada exitosamente", "success")
          fetchAreas()
        } catch (error) {
          console.error("Error deleting area:", error)
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Error al eliminar la Secretaría/Área",
          })
        }
      }
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(amount)
  }

  return (
    <div className="area-control-page">
      <div className="user-management-header">
        <h2>Control de Secretarías y Áreas</h2>
        <div className="search-and-add-container">
          <div className="search-box">
            <button className="btn-search">
              <FontAwesomeIcon icon={faSearch} />
            </button>
            <input
              type="text"
              className="input-search"
              placeholder="Buscar por nombre o monto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {/* Update 2: Removed amount search input */}
          </div>
          <button
            onClick={() => {
              setFormData({ name: "", type: "area", amount: "", from: "" })
              setShowCreateModal(true)
            }}
            className="add-user-btn"
          >
            Crear Nueva Secretaría/Area
          </button>
        </div>
      </div>
      <div className="table-container" style={{ maxHeight: "400px", overflowY: "auto" }}>
        <table className="users-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Presupuesto</th>
              <th>Secretaría Principal</th>
              <th>Usado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredAreas.map((area) => (
              <tr key={area.id}>
                <td>{area.name}</td>
                <td>{area.type === "area" ? "Secretaría" : "Área"}</td>
                <td>{formatCurrency(area.amount)}</td>
                <td>{area.from || "N/A"}</td>
                <td>{formatCurrency(area.used || 0)}</td>
                <td>
                  <button onClick={() => handleEdit(area)} className="edit-btn">
                    <HiOutlinePencil />
                  </button>
                  <button onClick={() => handleDelete(area.id)} className="delete-btn">
                    <HiOutlineTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCreateModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Crear Nueva Área/Subárea</h2>
            <form onSubmit={handleSubmit}>
              <label htmlFor="create-name">Nombre:</label>
              <input
                id="create-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nombre"
                required
              />
              <label htmlFor="create-type">Tipo:</label>
              <select id="create-type" name="type" value={formData.type} onChange={handleChange} required>
                <option value="area">Secretaría</option>
                <option value="subarea">Área</option>
              </select>
              <label htmlFor="create-amount">Presupuesto:</label>
              <input
                id="create-amount"
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Presupuesto"
                required
              />
              {formData.type === "subarea" && (
                <>
                  <label htmlFor="create-from">Secretaría a la que pertenece:</label>
                  <select id="create-from" name="from" value={formData.from} onChange={handleChange} required>
                    <option value="">Seleccionar secretaría</option>
                    {areas
                      .filter((area) => area.type === "area")
                      .map((area) => (
                        <option key={area.id} value={area.name}>
                          {area.name}
                        </option>
                      ))}
                  </select>
                </>
              )}
              <div className="btn-container-area">
                <button type="submit">Crear</button>
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Editar Área/Subárea</h2>
            <form onSubmit={handleUpdate}>
              <label htmlFor="edit-name">Nombre:</label>
              <input
                id="edit-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nombre"
                required
              />
              <label htmlFor="edit-type">Tipo:</label>
              <select id="edit-type" name="type" value={formData.type} onChange={handleChange} required>
                <option value="area">Secretaría</option>
                <option value="subarea">Área</option>
              </select>
              <label htmlFor="edit-amount">Presupuesto:</label>
              <input
                id="edit-amount"
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Presupuesto"
                required
              />
              {formData.type === "subarea" && (
                <>
                  <label htmlFor="edit-from">Secretaría a la que pertenece:</label>
                  <select id="edit-from" name="from" value={formData.from} onChange={handleChange} required>
                    <option value="">Seleccionar Área</option>
                    {areas
                      .filter((area) => area.type === "area")
                      .map((area) => (
                        <option key={area.id} value={area.name}>
                          {area.name}
                        </option>
                      ))}
                  </select>
                </>
              )}
              <div className="btn-container-area">
                <button type="submit">Actualizar</button>
                <button type="button" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

